/* 
 * Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
 * Distributed under the TruVideo proprietary license. 
 */
import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { authService } from '../services/authService';
import logo from '../assets/logo.png';
import isEmail from 'validator/lib/isEmail';
import { CheckCircle, Mail, ArrowLeft } from 'lucide-react';
import { useBranding } from '../hooks/useBranding';
import { getLoginPath, normalizeAccountSlug } from '../constants/loginRoutes';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [searchParams] = useSearchParams();
  // ?account=<slug> keeps the account login page branding and its "back to login" target
  const accountSlug = normalizeAccountSlug(searchParams.get('account'));
  const loginPath = accountSlug ? `/login/${encodeURIComponent(accountSlug)}` : getLoginPath();
  const { logoUrl: brandLogoUrl, name: brandName } = useBranding(accountSlug);

  const displayLogo = brandLogoUrl || logo;
  const displayLogoAlt = brandName ? `${brandName} Logo` : 'ServiceHub Logo';

  const validate = () => {
    if (!email.trim()) {
      setEmailError('Email is required');
      return false;
    }
    if (!isEmail(email)) {
      setEmailError('Invalid email format');
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleBlur = () => {
    if (!email.trim()) {
      setEmailError('Email is required');
    } else if (!isEmail(email)) {
      setEmailError('Invalid email format');
    } else {
      setEmailError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      await authService.forgotPassword(email.trim().toLowerCase());
      setSubmitted(true);
    } catch {
      // Always show success — prevents email enumeration
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden font-sans bg-base-200">
      {/* Background Mesh Gradients */}
      <div className="fixed inset-0 z-0 opacity-60">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-primary/10 blur-[120px] rounded-full" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-primary/5 blur-[120px] rounded-full" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60%] h-[60%] bg-secondary/5 blur-[150px] rounded-full" />
      </div>

      <div className="relative z-10 flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md flex flex-col items-center">

          {/* Brand Header */}
          <div className="text-center mb-8 animate-fade-in-down">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-2xl mb-4 shadow-xl border border-base-300 p-3 transition-transform duration-500 hover:scale-105">
              <img
                src={displayLogo}
                alt={displayLogoAlt}
                className="w-full h-full object-contain"
                onError={(e) => { e.currentTarget.src = logo; e.currentTarget.onerror = null; }}
              />
            </div>
            <h1 className="text-4xl font-black text-base-content mb-2 tracking-tighter leading-tight">
              {brandName || 'ServiceHub'}
            </h1>
            <p className="text-base-content/40 text-[10px] font-black tracking-[0.2em] uppercase">
              Service Manager Portal
            </p>
          </div>

          {/* Card */}
          <div className="w-full bg-base-100/80 backdrop-blur-2xl border border-base-300 rounded-3xl p-8 lg:p-10 shadow-[0_40px_80px_rgba(0,0,0,0.08)] relative group transition-all duration-500 hover:shadow-[0_50px_100px_rgba(0,0,0,0.12)]">
            <div className="absolute inset-0 bg-gradient-to-br from-white/40 to-transparent rounded-3xl pointer-events-none" />

            {submitted ? (
              /* ── Success State ── */
              <div className="relative z-10 flex flex-col items-center text-center space-y-6">
                <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-success" strokeWidth={2} />
                </div>
                <div>
                  <h2 className="text-xl font-black text-base-content tracking-tight uppercase mb-3">
                    Check your inbox
                  </h2>
                  <p className="text-sm text-base-content/60 leading-relaxed">
                    If an account with <span className="font-semibold text-base-content custom-line-break">{email}</span> exists, we've sent a password reset link. Check your spam folder if you don't see it.
                  </p>
                </div>
                <div className="w-full pt-2">
                  <Link
                    to={loginPath}
                    className="flex items-center justify-center gap-2 w-full btn-premium py-4 font-black tracking-widest uppercase text-sm"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Sign In
                  </Link>
                </div>
              </div>
            ) : (
              /* ── Form State ── */
              <div className="relative z-10">
                <h2 className="text-xl font-black text-base-content mb-2 tracking-tight uppercase">
                  Forgot Password
                </h2>
                <p className="text-sm text-base-content/50 mb-8 leading-relaxed">
                  Enter your workspace email and we'll send you a link to reset your password.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <label htmlFor="forgot-email" className="block text-[10px] font-black text-base-content/40 uppercase tracking-widest pl-1">
                      Workspace Email *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-base-content/30" />
                      <input
                        id="forgot-email"
                        type="email"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (emailError) setEmailError('');
                        }}
                        maxLength={100}
                        onBlur={handleBlur}
                        className={`w-full bg-base-100 border rounded-xl pl-10 pr-4 py-3.5 text-base-content text-sm placeholder-base-content/30 outline-none transition-all duration-300 focus:border-primary focus:ring-4 focus:ring-primary/10 ${emailError ? 'border-error ring-4 ring-error/10' : 'border-base-300'}`}
                        placeholder="your@email.com"
                      />
                    </div>
                    {emailError && <p className="text-error text-xs mt-1 font-medium">{emailError}</p>}
                  </div>

                  <button
                    type="submit"
                    id="forgot-password-submit"
                    disabled={loading}
                    className="w-full btn-premium py-4 font-black tracking-widest uppercase text-sm"
                  >
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </form>

                <div className="mt-6 text-center">
                  <Link
                    to={loginPath}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-base-content/40 hover:text-primary transition-colors uppercase tracking-widest"
                  >
                    <ArrowLeft className="w-3 h-3" />
                    Back to Sign In
                  </Link>
                </div>
              </div>
            )}
          </div>

          <p className="mt-10 text-base-content/20 text-[10px] font-black uppercase tracking-[0.25em]">
            &copy; {new Date().getFullYear()} ServiceHub • ENTERPRISE SECURE
          </p>
        </div>
      </div>
    </div>
  );
}
